import { Fragment, useState } from 'react';

import InfiniteScroll from '../../../../../../components/InfiniteScroll/InfiniteScroll';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { SaveButton } from '../../../../../../components/Clickables/Buttons/SaveButton';
import { BackButton } from '../../../../../../components/Clickables/Controls/BackButton/BackButton';
import { DownloadAppMapDrawerCTA } from '../../../../../../components/CTAs/DownloadAppMapDrawerCTA/DownloadAppMapDrawerCTA';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { useMapLayers } from '@domain/map/MapLayersContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { reportMykissClientError } from '@lib/errors/client';
import { getEdgesFromConnection } from '@graphql/connections/getEdgesFromConnection';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { getBoundingBoxFromLocation, useMapLocation } from '@domain/map/useMapLocation';
import { useUserInfo } from '@domain/user/useUserInfo';
import { GetTopSpeciesNearLocation } from '../../../../../../generated/graphql';
import { getTopSpeciesNearLocation } from '@graphql/queries/rutilus/Species';
import { ZOOM_IN_LIMIT, ZOOM_OUT_LIMIT } from '../../../../constants/constants';
import { useExplore } from '../../../../context/ExploreContext';
import {
  convertSpeciesIdArrayForUrl,
  getMonthArrayFromUrl,
  getSpeciesIdArrayFromUrl,
} from '../../../../context/helpers';
import { getFilterCount } from '../../helpers';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';

import { ClearFiltersButton } from './ClearFiltersButton/ClearFiltersButton';
import { FilterByMonth } from './FilterByMonth/FilterByMonth';
import { FilterBySpecies } from './FilterBySpecies/FilterBySpecies';
import { OnSpeciesClick, SingleSpeciesRow } from './SingleSpeciesRow/SingleSpeciesRow';

export const FilterCatchesView = () => {
  const [speciesFilter, setSpeciesFilter] = useState('');
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { toggleLayer, layers } = useMapLayers();
  const { isWholeLayerVisible } = layers.catchPositions;
  const { isPremium } = useUserInfo();
  const location = useMapLocation();
  const { currentPage } = useAnalytics();
  const { exploreQueryParams, saveParamsInUrl, filterViewBackButtonPath } = useExplore();
  const { catchFiltersMonths, catchFiltersSpecies } = exploreQueryParams;
  const catchesFilterMonthArray = getMonthArrayFromUrl(catchFiltersMonths);
  const catchesFilterSpeciesIdArray = getSpeciesIdArrayFromUrl(catchFiltersSpecies);
  const filterCount = getFilterCount({
    speciesIds: catchesFilterSpeciesIdArray,
    months: catchesFilterMonthArray,
  });

  const handleOnSpeciesClick: OnSpeciesClick =
    ({ speciesId, isSelected }) =>
    () => {
      if (isSelected && catchFiltersSpecies) {
        const newValue = catchesFilterSpeciesIdArray.filter(s => s !== speciesId);
        saveParamsInUrl({
          catchFiltersSpecies: convertSpeciesIdArrayForUrl(newValue),
        });
      } else {
        if (!isWholeLayerVisible && isPremium) {
          toggleLayer('catchPositions');
        }
        const newValue = [...catchesFilterSpeciesIdArray, speciesId];
        saveParamsInUrl({
          catchFiltersSpecies: convertSpeciesIdArrayForUrl(newValue),
        });
      }
    };

  const isTooZoomedIn = !!location && location.zoom > ZOOM_IN_LIMIT;
  const isTooZoomedOut = !!location && location.zoom < ZOOM_OUT_LIMIT;
  const isZoomWithinRange = !isTooZoomedIn && !isTooZoomedOut;
  const isTooZoomedInAndNotPro = isTooZoomedIn && !isPremium;

  // We block users from zooming right in if not pro to prevent a pro leak
  // If we don't block them they can zoom all the way in to pinpoint the catch
  const skipRutilusQuery = !location || isTooZoomedOut || isTooZoomedInAndNotPro;

  const boundingBox = getBoundingBoxFromLocation(location);

  const {
    data,
    loading: isLoadingSpecies,
    fetchMore,
  } = useRutilusQuery(getTopSpeciesNearLocation, {
    skip: skipRutilusQuery,
    variables: {
      first: 12,
      boundingBox,
    },
  });

  const species = getEdgesFromConnection(data?.topSpeciesNearLocation);

  if (isLoadingSpecies) {
    return (
      <MapDrawerLayout>
        <LoadingSpinner loadingText="Loading data for filters..." />
      </MapDrawerLayout>
    );
  }

  const hasNextPage = data?.topSpeciesNearLocation.pageInfo.hasNextPage ?? false;
  const endCursor = data?.topSpeciesNearLocation.pageInfo.endCursor;

  const fetchNextPage = async (): Promise<unknown> => {
    trackEvent({
      name: 'mykiss_load_more',
      properties: {
        page: currentPage,
        manual: true,
        source: 'explore_catches_filters_species',
      },
    });

    setIsFetchingMore(true);

    return fetchMore({
      variables: {
        after: endCursor,
        first: 12,
        boundingBox: getBoundingBoxFromLocation(location),
      },
      updateQuery: (
        prev: GetTopSpeciesNearLocation,
        { fetchMoreResult },
      ): GetTopSpeciesNearLocation => {
        return {
          ...fetchMoreResult,
          topSpeciesNearLocation: {
            ...fetchMoreResult.topSpeciesNearLocation,
            edges: [
              ...(prev.topSpeciesNearLocation.edges ?? []),
              ...(fetchMoreResult.topSpeciesNearLocation.edges ?? []),
            ],
            pageInfo: {
              ...fetchMoreResult.topSpeciesNearLocation.pageInfo,
            },
          },
        };
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissClientError);
  };

  const filteredSpeciesData = species
    .map(({ catchesCount, node }) => {
      const images = getNodesFromConnection(node?.exampleCatchWithImage?.post.images);
      return {
        name: node?.displayName ?? '',
        totalCount: catchesCount,
        speciesId: node?.externalId ?? '',
        imageProps: {
          src: images.pop()?.url ?? '',
        },
      };
    })
    .filter(({ name }) =>
      name.toLocaleLowerCase().trim().includes(speciesFilter.toLowerCase().trim()),
    );

  const headerContent = (
    <div className="mb-4 flex w-full items-center justify-between">
      <BackButton
        variant="controlLakeFogBg"
        onClick={() => {
          saveParamsInUrl({
            drawerView: filterViewBackButtonPath,
          });
        }}
      />
      <ClearFiltersButton filterCount={filterCount} />
    </div>
  );

  const footerContent = (
    <div className="mt-3 flex w-full justify-center">
      <SaveButton
        onClick={() => {
          saveParamsInUrl({
            drawerView: 'catches',
          });
        }}
        className="w-full"
      >
        Show Catches
      </SaveButton>
    </div>
  );

  if (isTooZoomedInAndNotPro) {
    return (
      <MapDrawerLayout
        headerContent={
          <div className="mb-4 flex w-full items-center justify-between">
            <BackButton
              variant="controlLakeFogBg"
              onClick={() => {
                saveParamsInUrl({
                  drawerView: filterViewBackButtonPath,
                });
              }}
            />
          </div>
        }
      >
        <DownloadAppMapDrawerCTA />
      </MapDrawerLayout>
    );
  }

  return (
    <MapDrawerLayout
      withCloseButton={false}
      headerContent={headerContent}
      footerContent={footerContent}
      contentId="filter-catches-list"
    >
      <FilterByMonth />
      <FilterBySpecies onChange={setSpeciesFilter} speciesFilter={speciesFilter} />
      {isTooZoomedOut && (
        <MapDrawerNotification
          title="Zoom in"
          description="Try zooming into an area to see species you can filter by"
        />
      )}
      {filteredSpeciesData.length === 0 && isZoomWithinRange && (
        <MapDrawerNotification
          title="No results found"
          description="Try another area or zooming out to get more results"
        />
      )}
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={filteredSpeciesData.length}
        hasMore={!isFetchingMore && hasNextPage}
        scrollableTarget="filter-catches-list"
        loader={<></>}
      >
        <div className="devide-y bg-sea-foam mb-2 grid grid-cols-1 rounded-md md:rounded-lg">
          {filteredSpeciesData.map(({ name, totalCount, imageProps, speciesId }, index) => (
            <Fragment key={speciesId}>
              <SingleSpeciesRow
                id={speciesId}
                name={name}
                totalCount={totalCount}
                imageSrc={imageProps.src}
                isSelected={catchesFilterSpeciesIdArray.some(s => s === speciesId)}
                onClick={handleOnSpeciesClick}
              />
              {index !== filteredSpeciesData.length - 1 && <hr className="border-twilight mx-3" />}
            </Fragment>
          ))}
        </div>
        {filteredSpeciesData.length > 0 && hasNextPage && (
          <div className="my-8 flex w-full justify-center">
            <SaveButton
              onClick={fetchNextPage}
              variant="buttonPrimary"
              savingLabel="Loading..."
              isSaving={isFetchingMore}
            >
              Load more
            </SaveButton>
          </div>
        )}
      </InfiniteScroll>
    </MapDrawerLayout>
  );
};
